import { DateTime } from 'luxon'
import { durationText } from 'utils/durationUtils'

export const findMatchingSlotIso = (iso, slots = []) => {
  if (!iso || !slots.length) return ''
  if (slots.includes(iso)) return iso

  const target = DateTime.fromISO(iso)
  if (!target.isValid) return ''

  const match = slots.find(slot => {
    const slotTime = DateTime.fromISO(slot)
    return slotTime.isValid && slotTime.toMillis() === target.toMillis()
  })

  return match || ''
}

export const mergePresetSlotForDate = (
  slots,
  presetStartDateTime,
  selectedDate,
  timezone,
) => {
  if (!presetStartDateTime || !selectedDate) return slots

  const preset = DateTime.fromISO(presetStartDateTime, { zone: timezone })
  if (!preset.isValid || preset.toISODate() !== selectedDate) return slots

  const presetIso = preset.toISO()
  if (findMatchingSlotIso(presetIso, slots)) return slots

  return [...slots, presetIso].sort(
    (a, b) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis(),
  )
}

export const duplicateLessonsWithMultipleLocations = lessonTypes =>
  lessonTypes.flatMap(lessonType =>
    lessonType.locations.map(loc => ({
      ...lessonType,
      locationPlace: `${loc?.city}, ${loc?.state}`,
      locationName: loc.name,
      locationId: loc.id,
    })),
  )

export const formatLessonCardMeta = (lesson, creditQuantity) => {
  const parts = [durationText(lesson.durationInMinutes)].filter(Boolean)

  // Only pack lesson types are always booked with a credit; every other
  // lesson keeps its price because the coach can choose not to use a credit.
  const isBookedWithCredit =
    creditQuantity > 0 && lesson.lessonPacks?.length > 0

  if (!isBookedWithCredit && lesson.priceInCents != null) {
    parts.push(`$${(lesson.priceInCents / 100).toFixed(0)}`)
  }

  return parts.join(' · ')
}

// Lesson types sold as packs are only bookable here with a pack credit the
// student already owns.
export const filterLessonsForOffering = (
  flatLessons,
  offering,
  OFFERING_TYPES,
  lessonCreditQuantities = {},
) => {
  if (offering === OFFERING_TYPES.PRIVATE) {
    return flatLessons.filter(
      lesson =>
        lesson.lessonPacks?.length === 0 ||
        lessonCreditQuantities[lesson.id] > 0,
    )
  }
  return flatLessons
}

export const sortLessonsWithCreditsFirst = (lessons, lessonCreditQuantities) =>
  [...lessons].sort(
    (a, b) =>
      Number(lessonCreditQuantities[b.id] > 0) -
      Number(lessonCreditQuantities[a.id] > 0),
  )

export const selectVisibleOfferingToggleOptions = ({
  options,
  offeringsLoaded,
  privateLessonCount,
  groupEventCount,
  OFFERING_TYPES,
}) => {
  if (!offeringsLoaded) return options
  return options.filter(option => {
    if (option.value === OFFERING_TYPES.PRIVATE) {
      return privateLessonCount > 0
    }
    if (option.value === OFFERING_TYPES.EVENT) {
      return groupEventCount > 0
    }
    return false
  })
}

export const hasAnyOfferingToggleCategory = (
  privateLessonCount,
  groupEventCount,
) => privateLessonCount > 0 || groupEventCount > 0

export const resolveOfferingForAvailableCategories = ({
  offering,
  privateLessonCount,
  groupEventCount,
  OFFERING_TYPES,
}) => {
  if (
    offering === OFFERING_TYPES.EVENT &&
    groupEventCount === 0 &&
    privateLessonCount > 0
  ) {
    return OFFERING_TYPES.PRIVATE
  }
  if (
    offering !== OFFERING_TYPES.EVENT &&
    privateLessonCount === 0 &&
    groupEventCount > 0
  ) {
    return OFFERING_TYPES.EVENT
  }
  return offering
}

export const OFFERING_LIST_COLLAPSED_COUNT = 4

// A selected offering past the limit (for example from a deep link) keeps the
// full list open so the selection stays visible.
export const getVisibleOfferings = (
  items,
  { expanded = false, isSelected = () => false } = {},
) => {
  const limit = OFFERING_LIST_COLLAPSED_COUNT
  if (expanded || items.length <= limit) {
    return { visible: items, hiddenCount: 0 }
  }
  if (items.findIndex(isSelected) >= limit) {
    return { visible: items, hiddenCount: 0 }
  }
  return { visible: items.slice(0, limit), hiddenCount: items.length - limit }
}
